<template lang="pug">
    div.markets.pt-1

        div.marketsHeader

            // Title
            title-component(type="h3" title="Markets Config")

            // Frequency update
            frequency-update-component(
                :frequency="refreshTime"
                :refreshAction="frequencyCallback"
            )

        div.marketsContent

            // Credentials config
            market-config(v-if="!hasSavedCredentials")

            div(v-else)
                v-radio-group.noDirty.mb-3(
                    v-model="selectedSymbologie"
                    hide-details
                    label="Select symbologie system"
                    row
                )
                    v-radio.pl-1(
                        v-for="symbologie in symbologies"
                        :key="symbologie.url_name"
                        :label="symbologie.display_name"
                        :value="symbologie")

                // Search field
                v-text-field(
                    outlined
                    :disabled="!selectedSymbologie"
                    v-model="prefix"
                    @input="searchSymbols"
                    label="Prepend inner"
                    append-icon="search"
                )

                title-component(type="h5" title="Available List")

                v-simple-table.c-table(v-if="symbolsList && symbolsList.length")
                    template(v-slot:default)
                        tbody
                            tr(v-for="item in symbolsList")
                                td.pl-0 {{ item.symbol }}
                                td.pl-0 {{ item.description }}
                                td.text-right
                                    v-btn.mr-2(
                                        v-if="item.asset_class === 'FUT'"
                                        @click="addGraphElement(item)"
                                        color="primary"
                                        outlined
                                        rounded
                                        x-small
                                    )
                                        v-icon(color="primary") add
                                        span to graph

                                    v-btn(
                                        @click="addElement(item)"
                                        color="secondary"
                                        outlined
                                        rounded
                                        x-small
                                    )
                                        v-icon(color="primary") add
                                        span to list

                div(v-else) Please add prefix to search field

                div.full-width
                    separator-component.grey

                title-component(type="h5" title="Selected List")

                // Drag and Drop
                draggable-component(:items="symbolsSelected" :callBackFunction="draggableCallback" keyValue="symbol")

                div.full-width
                    separator-component.grey

                title-component(type="h5" title="Selected items for Graph")

                // Drag and Drop
                draggable-component(:items="symbolsGraphSelected" :callBackFunction="draggableGraphCallback" keyValue="product_symbol")

                v-divider.my-3

                div.text-right

                    // Submit button
                    v-btn.mr-0.submit(
                        type="submit"
                        color="primary"
                        large
                        @click="updateWatchlist"
                        :isLoading="isLoading"
                    ) {{ $t('fields.save') }}
</template>

<script>
    import {
        FrequencyUpdate,
        Title,
        DragAndDrop,
        Separator,
    } from '~components';
    import { mapGetters, mapActions } from 'vuex';
    import { markets } from '~api';
    import { Handler } from '~services';
    import MarketConfig from './MarketConfig';
    import debounce from 'lodash/debounce';

    export default {
        components: {
            MarketConfig,
            DraggableComponent: DragAndDrop,
            FrequencyUpdateComponent: FrequencyUpdate,
            TitleComponent: Title,
            SeparatorComponent: Separator,
        },
        data() {
            return {
                symbolsList: [],
                symbolsSelected: [],
                symbologies: [],
                isLoading: false,
                prefix: '',
                hasSavedCredentials: null,
                selectedSymbologie: null,
            };
        },
        computed: {
            ...mapGetters('markets', ['refreshTime']),
        },
        async created() {
            this.getRefreshTime();

            const [
                respHasSavedCredentials,
                respSelectedSymbols,
                respSelectedGraphSymbols,
                symbologiesResp,
            ] = await Promise.all([
                markets.hasSavedCredentials(),
                markets.getMarketFields(),
                markets.getMarketGraphFields(),
                markets.getSymbologies(),
            ]);

            if (respHasSavedCredentials.ok)
                this.hasSavedCredentials =
                    respHasSavedCredentials.data === 'password_set'
                        ? true
                        : false;

            if (respSelectedSymbols.ok)
                this.symbolsSelected = respSelectedSymbols.data.map((item) =>
                    JSON.parse(item)
                );

            if (respSelectedGraphSymbols.ok)
                this.symbolsGraphSelected = respSelectedGraphSymbols.data.map(
                    (item) => JSON.parse(item)
                );

            if (symbologiesResp.ok) this.symbologies = symbologiesResp.data;
        },
        methods: {
            ...mapActions('markets', ['getRefreshTime', 'setRefreshTime']),
            ...mapActions('snackbar', ['showMessage', 'showWarningMessage']),

            searchSymbols: debounce(function () {
                this.search();
            }, 400),
            async search() {
                if (this.prefix.length < 2) return;

                const resp = await markets.getMarketCredentials(
                    this.selectedSymbologie?.url_name,
                    this.prefix
                );

                if (resp.ok) {
                    this.symbolsList = resp.data.map((item) => {
                        return {
                            ...item,
                            ...this.selectedSymbologie,
                        };
                    });
                }
            },

            /**
             * Set refresh time
             * @param e
             */
            frequencyCallback(e) {
                this.setRefreshTime(e);
            },

            addElement(element) {
                if (
                    this.symbolsSelected.find(
                        (item) => item.symbol === element.symbol
                    )
                ) {
                    return this.showWarningMessage('Symbol already added');
                }
                this.symbolsSelected.push(element);
                return this.showMessage('Symbol added to selected list');
            },

            addGraphElement(element) {
                if (
                    this.symbolsGraphSelected.find(
                        (item) => item.product_symbol === element.product_symbol
                    )
                ) {
                    return this.showWarningMessage(
                        'Product symbol already added'
                    );
                }
                this.symbolsGraphSelected.push(element);
                return this.showMessage(
                    'Symbol added to selected list for Graph'
                );
            },

            /**
             * Set sorted symbols from draggble
             * @param sortedItems
             */
            draggableCallback(sortedItems) {
                this.symbolsSelected = sortedItems;
            },

            /**
             * Set sorted symbols from draggble
             * @param sortedItems
             */
            draggableGraphCallback(sortedItems) {
                this.symbolsGraphSelected = sortedItems;
            },

            async updateWatchlist() {
                const successMessage = this.$t(
                    'markets.updateWatchlistSuccessMessage'
                );
                this.isLoading = true;

                const params = this.symbolsSelected.map((item) => {
                    return {
                        product_symbol: item.product_symbol,
                        security_exchange: item.security_exchange,
                        symbol: item.symbol,
                        symbology: `${
                            item.symbology ? item.symbology : item.fix_code
                        }`,
                        asset_class: item.asset_class,
                    };
                });

                const paramsGraph = this.symbolsGraphSelected.map((item) => {
                    return {
                        product_symbol: item.product_symbol,
                        security_exchange: item.security_exchange,
                    };
                });

                const resp = await markets.updateMarketFields(params);
                const respGraph = await markets.updateMarketGraphFields(
                    paramsGraph
                );

                Handler.handleResponse(resp || respGraph, successMessage);

                this.isLoading = false;
            },
        },
    };
</script>

<style lang="stylus" scoped>
    .marketsHeader
        display: flex
        align-items: center
        justify-content: space-between

    .addButton
        cursor: pointer
        transform: opacity 0.3s

        &:hover
            opacity: 0.8

    .noDirty
        >>> label
                transform: scale(1) !important
</style>
